import React, { useState, useRef, useEffect } from "react";
import {
  Save,
  Eye,
  Upload,
  X,
  Calendar,
  User,
  Type,
  FileText,
  Tag,
  Link,
  Image as ImageIcon,
} from "lucide-react";
import AddBlogContentForm from "./AddBlogContentForm";
import { postBlogAPI, updateBlogAPI } from "../services/apicollection";

const GENRES = [
  "Web Development",
  "AI & Machine Learning",
  "Mobile Development",
  "DevOps",
  "UI/UX Design",
  "Data Science",
  "Cybersecurity",
  "Cloud Computing",
  "Blockchain",
  "Backend Development",
  "Digital Marketing",
  "Data Analytics",
  "Dsa",
  "Full Stack Development",
];

const LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];

const initialFormState = (initialData) => ({
  title: initialData?.title || "",
  genre: initialData?.genre || "",
  desc: initialData?.desc || "",
  content: initialData?.content || "",
  author: initialData?.author || "Mindcoders",
  date: initialData?.date || new Date().toISOString().split("T")[0],
  slug: initialData?.slug || "",
  readTime: initialData?.readTime || "8",
  views: initialData?.views || "0",
  likes: initialData?.likes || "0",
  comments: initialData?.comments || "0",
  rating: initialData?.rating || "4.8",
  reviewCount: initialData?.reviewCount || "0",
  level: initialData?.level || "Beginner",
  tags: Array.isArray(initialData?.tags) ? [...initialData.tags] : [],
  poster: initialData?.poster || null,
});

const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

// Shared preview content styles applied wherever blog HTML is rendered
const PREVIEW_CONTENT_STYLES = {
  className: [
    "reader-content text-sm text-slate-300",
    // headings
    "[&_h1]:mb-3 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-slate-100",
    "[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-100",
    "[&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-200",
    "[&_h4]:mb-1 [&_h4]:font-bold [&_h4]:text-slate-200",
    "[&_h5]:mb-1 [&_h5]:font-semibold [&_h5]:text-slate-200",
    "[&_h6]:mb-1 [&_h6]:font-semibold [&_h6]:text-slate-300",
    // paragraphs & bold
    "[&_p]:mb-2 [&_p]:leading-relaxed",
    "[&_strong]:font-bold [&_strong]:text-slate-100",
    // blockquote
    "[&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-400 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500",
    // highlight
    "[&_.lead]:my-2 [&_.lead]:rounded-lg [&_.lead]:bg-amber-50 [&_.lead]:px-4 [&_.lead]:py-3 [&_.lead]:text-amber-700",
    // code
    "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-[#13233a] [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-xs",
    // list
    "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1",
    // image
    "[&_figure]:my-4",
    "[&_img]:max-w-full [&_img]:rounded-lg",
    "[&_figcaption]:mt-1 [&_figcaption]:text-xs [&_figcaption]:text-slate-400",
    // table
    "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
    "[&_th]:border [&_th]:border-[#1d3350] [&_th]:bg-[#0d1b2e] [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-300",
    "[&_td]:border [&_td]:border-[#1d3350] [&_td]:px-4 [&_td]:py-2 [&_td]:text-slate-400",
    "[&_tr:nth-child(even)_td]:bg-[#0d1b2e]/50",
  ].join(" "),
};

const BlogForm = ({ onSave, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState(() => initialFormState(initialData));
  const [previewMode, setPreviewMode] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [currentTag, setCurrentTag] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "title" && { slug: generateSlug(value) }),
    }));
    setError(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData((prev) => ({
          ...prev,
          poster: { file, url: ev.target?.result, name: file.name },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData((prev) => ({
          ...prev,
          poster: { file, url: ev.target?.result, name: file.name },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addTag = () => {
    const tag = currentTag.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      setCurrentTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const content = formData.content || "";
      const blogPayload = {
        ...formData,
        content,
        tags: formData.tags,
        blog: formData.poster?.file,
      };
      delete blogPayload.poster;

      if (isEditing) {
        const updatePayload = { ...blogPayload };
        delete updatePayload.poster;
        const res = await updateBlogAPI(initialData._id, updatePayload);
        if (res?.status === false) throw new Error(res.message);
        onSave();
      } else {
        const payload = { ...blogPayload };
        delete payload.poster;
        if (formData.poster?.file) payload.blog = formData.poster.file;
        const res = await postBlogAPI(payload);
        if (res?.status === false) throw new Error(res.message);
        onSave();
      }
      localStorage.removeItem("content");
    } catch (err) {
      setError(err.message || "Failed to save blog");
    } finally {
      setSubmitting(false);
    }
  };

  const posterSrc =
    formData.poster?.url ??
    (typeof formData.poster === "string" ? formData.poster : null);

  const renderPreview = () => (
    <div className="min-h-screen bg-[#0d1b2e]">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-6">
          {posterSrc && (
            <img
              src={posterSrc}
              alt={formData.title}
              className="mb-6 h-64 w-full rounded-lg object-cover"
            />
          )}
          <span className="mb-4 inline-block rounded-full bg-slate-800 px-3 py-1 text-sm font-medium text-white">
            {formData.genre}
          </span>
          <h1 className="mb-4 text-2xl font-bold text-slate-100">
            {formData.title}
          </h1>
          <div className="mb-4 flex flex-wrap gap-4 text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar size={16} />
              {new Date(formData.date).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <User size={16} />
              {formData.author}
            </span>
          </div>
          <p className="mb-6 text-slate-300">{formData.desc}</p>

          {/* ── Blog content with full bold + table + all block styles ── */}
          <div
            className={PREVIEW_CONTENT_STYLES.className}
            dangerouslySetInnerHTML={{ __html: formData.content || "" }}
          />

          {formData.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2 border-t border-[#1d3350] pt-6">
              {formData.tags.map((tag, i) => (
                <span
                  key={i}
                  className="rounded-full bg-slate-200 px-3 py-1 text-sm text-slate-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    return () => { localStorage.removeItem("content"); };
  }, []);

  if (previewMode) {
    return (
      <div className="min-h-screen bg-[#0d1b2e]">
        <div className="sticky top-0 z-40 border-b border-[#1d3350] bg-[#0f1c2e]">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
            <h2 className="text-lg font-semibold text-slate-200">Preview</h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                className="rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300 hover:bg-[#0d1b2e]"
              >
                Back to edit
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300 hover:bg-[#0d1b2e]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        {renderPreview()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1b2e]">
      <div className="sticky top-0 z-40 border-b border-[#1d3350] bg-[#0f1c2e]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <h1 className="text-xl font-bold text-slate-200">
            {isEditing ? "Edit Blog" : "New Blog Post"}
          </h1>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPreviewMode(true)}
              className="flex items-center gap-2 rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300 hover:bg-[#0d1b2e]"
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300 hover:bg-[#0d1b2e]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            {/* ── Left column ── */}
            <div className="space-y-6 lg:col-span-2">
              {/* Title */}
              <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Type size={18} /> Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Blog title..."
                  className="w-full rounded-lg border border-[#1d3350] px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  required
                />
              </div>

              {/* Description */}
              <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <FileText size={18} /> Description
                </label>
                <textarea
                  value={formData.desc}
                  onChange={(e) => handleInputChange("desc", e.target.value)}
                  placeholder="Short description..."
                  rows={3}
                  className="w-full resize-none rounded-lg border border-[#1d3350] px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  required
                />
              </div>

              {/* Content builder */}
              <div>
                <AddBlogContentForm
                  setContent={(content) => handleInputChange("content", content)}
                  initialContent={formData.content}
                />
              </div>

              {/* Tags */}
              <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Tag size={18} /> Tags
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addTag())
                    }
                    placeholder="Add tag..."
                    className="flex-1 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600"
                  >
                    Add
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-slate-500 hover:text-slate-300"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right column ── */}
            <div className="space-y-6">
              {/* Poster */}
              <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <ImageIcon size={18} /> Poster image
                </label>
                <div
                  className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                    dragActive ? "border-slate-500 bg-[#0d1b2e]" : "border-[#1d3350]"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {posterSrc ? (
                    <div className="space-y-2">
                      <img
                        src={posterSrc}
                        alt="Preview"
                        className="mx-auto h-32 rounded-lg object-cover"
                      />
                      <p className="text-xs text-slate-400">{formData.poster?.name}</p>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, poster: null }))}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload size={40} className="mx-auto text-slate-400" />
                      <p className="mt-2 text-sm text-slate-400">Drop image or</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-1 text-sm font-medium text-slate-300 underline hover:no-underline"
                      >
                        Browse
                      </button>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Details */}
              <div className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4">
                <label className="mb-3 block text-sm font-medium text-slate-200">
                  Details
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Genre</label>
                    <select
                      value={formData.genre}
                      onChange={(e) => handleInputChange("genre", e.target.value)}
                      className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select</option>
                      {GENRES.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Author</label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => handleInputChange("author", e.target.value)}
                      className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange("date", e.target.value)}
                      className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Level</label>
                    <select
                      value={formData.level}
                      onChange={(e) => handleInputChange("level", e.target.value)}
                      className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                    >
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Read time (min)</label>
                    <input
                      type="number"
                      value={formData.readTime}
                      onChange={(e) => handleInputChange("readTime", e.target.value)}
                      min={1}
                      className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Slug</label>
                    <div className="flex items-center gap-1">
                      <Link size={14} className="text-slate-400" />
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => handleInputChange("slug", e.target.value)}
                        placeholder="auto-from-title"
                        className="w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-800 py-3 font-medium text-white hover:bg-slate-700 disabled:opacity-60"
              >
                <Save size={18} />
                {submitting ? "Saving..." : isEditing ? "Update" : "Publish"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogForm;